import React, { useEffect, useMemo, useState } from "react";
import {
  ResponsiveContainer,
  ComposedChart, Bar,
  CartesianGrid, XAxis, YAxis, Tooltip, Legend, LabelList,
} from "recharts";
import SearchSelect from "@/components/SearchSelect";
import { api, fmtNumber } from "@/lib/api";
import { X } from "@phosphor-icons/react";

const NAVY = "#0d3880";
// Fixed semantic palette for the three target tiers — never overlaps with
// the navy "Sales" bar so users can quickly tell each apart at a glance.
const T1 = "#d97706"; // amber-600 · warning / floor target
const T2 = "#059669"; // emerald-600 · success / committed target
const T3 = "#6366f1"; // indigo-500 · stretch / aspirational target
const GRID = "rgba(71,85,105,0.15)";

const RANGES = [
  { k: "ytd", label: "YTD" },
  { k: "mtd", label: "MTD" },
  { k: "qtd", label: "QTD" },
  { k: "last_7", label: "Last 7 days" },
  { k: "full", label: "Full year" },
  { k: "custom", label: "Custom…" },
];

/**
 * Target vs Sales comparison chart for the Dashboard.
 *
 * Self-contained: takes `year` + `tier` from the parent and fetches its OWN
 * `/analytics/target-progress` data when the date range changes. This keeps
 * the chart independent of the table above it (which has its own filters).
 *
 * - Multi-select location chips. Defaults to ALL locations with a target.
 * - Bar / Line toggle.
 * - Achievement % is rendered as a top-anchored label above each pair so it's
 *   readable regardless of bar height (very short bars previously hid white
 *   in-bar labels).
 */
const TargetCompareChart = ({ year, tier }) => {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selected, setSelected] = useState([]); // location codes; [] = ALL
  const [rangeKind, setRangeKind] = useState("ytd");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [windowInfo, setWindowInfo] = useState(null);

  // Refetch whenever year / tier / date range changes.
  useEffect(() => {
    const ctrl = new AbortController();
    setLoading(true);
    const p = new URLSearchParams();
    p.append("year", year);
    p.append("tier", tier);
    p.append("range_kind", rangeKind);
    if (rangeKind === "custom") {
      if (startDate) p.append("start_date", startDate);
      if (endDate) p.append("end_date", endDate);
    }
    api.get(`/analytics/target-progress?${p.toString()}`, { signal: ctrl.signal })
      .then((r) => {
        setRows(r.data.rows || []);
        setWindowInfo(r.data.window || null);
      })
      .catch(() => {})
      .finally(() => setLoading(false));
    return () => ctrl.abort();
  }, [year, tier, rangeKind, startDate, endDate]);

  // Locations with at least ONE target tier set (T1 or T2 or T3). The
  // server's `target_amount` is just the chosen-tier value, so a row with
  // only T1 populated would show `target_amount=0` for tier=2 — but it
  // still has a target and should appear here.
  const targeted = useMemo(
    () => rows.filter((r) =>
      (r.target_1 || 0) > 0 || (r.target_2 || 0) > 0 || (r.target_3 || 0) > 0,
    ),
    [rows],
  );

  // No auto-selection — start empty so the user explicitly picks who to
  // compare. The dropdown still shows ALL targeted locations (sorted by
  // largest tier) and excludes anything already picked so the same
  // location can't be added twice.
  const allCodes = useMemo(
    () => targeted.slice().sort((a, b) => {
      const aMax = Math.max(a.target_1 || 0, a.target_2 || 0, a.target_3 || 0);
      const bMax = Math.max(b.target_1 || 0, b.target_2 || 0, b.target_3 || 0);
      return bMax - aMax;
    }).map((r) => r.location_code),
    [targeted],
  );
  const effectiveSelected = selected;

  const data = useMemo(() => {
    return effectiveSelected
      .map((code) => targeted.find((r) => String(r.location_code) === String(code)))
      .filter(Boolean)
      .map((r) => {
        // Round to 1 decimal so very small achievements show "0.5%"
        // instead of collapsing to "0%".
        const pct = (n) => Math.round((n ?? 0) * 10) / 10;
        const lbl = (n) => `${pct(n).toFixed(pct(n) >= 10 ? 0 : 1)}%`;
        return {
          location_code: r.location_code,
          location_name: r.location_name,
          short: (r.location_name || r.location_code || "").slice(0, 18),
          sales: Math.round(r.ytd_net_value || 0),
          target_1: Math.round(r.target_1 || 0),
          target_2: Math.round(r.target_2 || 0),
          target_3: Math.round(r.target_3 || 0),
          ach_t1: pct(r.achievement_pct_t1),
          ach_t2: pct(r.achievement_pct_t2),
          ach_t3: pct(r.achievement_pct_t3),
          ach_t1_label: lbl(r.achievement_pct_t1),
          ach_t2_label: lbl(r.achievement_pct_t2),
          ach_t3_label: lbl(r.achievement_pct_t3),
          // Backwards-compatible — used by the inside-bar % label on the
          // Sales bar so a single number summarises against the chosen tier.
          pct_label: lbl(r.achievement_pct),
        };
      });
  }, [targeted, effectiveSelected]);

  const addLocation = (code) => {
    if (!code) return;
    if (selected.includes(code)) return;
    setSelected([...selected, code]);
  };
  const removeLocation = (code) => setSelected(selected.filter((c) => c !== code));

  // Wider per-location footprint since we now render 4 bars (Sales + T1/T2/T3)
  // instead of 2. Caps at a reasonable height — horizontal scroll handles wide.
  const dynamicHeight = Math.max(420, Math.min(900, 80 + data.length * 28));

  return (
    <div data-testid="target-compare-chart">
      {/* ── Date range selector ─────────────────────────────────────── */}
      <div className="flex flex-wrap items-center gap-2 mb-3" data-testid="tc-range">
        <span className="text-[10px] uppercase tracking-[0.22em] text-myb-500 font-semibold">Period</span>
        <div className="flex border hairline bg-white rounded-sm overflow-hidden">
          {RANGES.map(({ k, label }) => (
            <button
              key={k}
              data-testid={`tc-range-${k}`}
              onClick={() => setRangeKind(k)}
              className={`px-2.5 py-1.5 text-[11px] uppercase tracking-wider font-semibold transition-colors ${
                rangeKind === k ? "bg-myb-500 text-white" : "text-muted-foreground hover:text-myb-500"
              }`}
            >{label}</button>
          ))}
        </div>
        {rangeKind === "custom" && (
          <>
            <input
              data-testid="tc-start"
              type="date"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
              className="bg-white border border-border focus:border-myb-500 rounded-sm px-2 py-1.5 text-xs outline-none"
            />
            <span className="text-xs text-muted-foreground">→</span>
            <input
              data-testid="tc-end"
              type="date"
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
              className="bg-white border border-border focus:border-myb-500 rounded-sm px-2 py-1.5 text-xs outline-none"
            />
          </>
        )}
        {windowInfo?.start && windowInfo?.end && (
          <span className="text-[10px] uppercase tracking-wider text-muted-foreground/80 ml-1 font-mono">
            {windowInfo.start} → {windowInfo.end}
            {windowInfo.days ? ` · ${windowInfo.days} day${windowInfo.days === 1 ? "" : "s"}` : ""}
          </span>
        )}
      </div>

      {/* ── Top row: location picker + bar/line toggle ──────────────── */}
      <div className="flex flex-wrap items-center gap-3 mb-3">
        <SearchSelect
          testId="tc-add-location"
          width="w-[260px]"
          height="h-9"
          placeholder="Add a location to compare…"
          value=""
          onChange={(v) => addLocation(v)}
          options={targeted
            .filter((r) => !selected.includes(r.location_code))
            .sort((a, b) => (a.location_name || "").localeCompare(b.location_name || ""))
            .map((r) => ({
              value: r.location_code,
              label: r.location_name || r.location_code,
              sub: r.location_code,
            }))}
        />
      </div>

      {/* ── Selected location chips ─────────────────────────────────── */}
      {effectiveSelected.length > 0 && (
        <div className="flex flex-wrap items-center gap-1.5 mb-3" data-testid="tc-chips">
          <span className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground/80 font-semibold mr-1">
            {effectiveSelected.length} selected · pool of {allCodes.length} targeted
          </span>
          {effectiveSelected.map((code) => {
            const r = targeted.find((x) => String(x.location_code) === String(code));
            if (!r) return null;
            return (
              <span key={code} className="inline-flex items-center gap-1 text-[11px] bg-myb-50 text-myb-500 border border-myb-200 rounded-full pl-2.5 pr-1 py-0.5 font-medium">
                {r.location_name || code}
                <button
                  type="button"
                  onClick={() => removeLocation(code)}
                  className="rounded-full hover:bg-myb-100 p-0.5"
                  aria-label={`Remove ${r.location_name}`}
                ><X size={10} weight="bold" /></button>
              </span>
            );
          })}
          <button
            type="button"
            onClick={() => setSelected([])}
            data-testid="tc-clear"
            className="text-[10px] uppercase tracking-wider text-muted-foreground hover:text-myb-500 font-semibold ml-1"
          >Clear all</button>
        </div>
      )}

      {/* ── Chart ───────────────────────────────────────────────────── */}
      {loading ? (
        <div className="py-12 text-center text-sm text-muted-foreground italic">Loading…</div>
      ) : data.length === 0 ? (
        <div className="py-12 text-center text-sm text-muted-foreground italic">
          {targeted.length === 0
            ? "No locations have a target set for this period."
            : `Pick one or more locations from the dropdown above to compare against their targets. ${allCodes.length} location${allCodes.length === 1 ? "" : "s"} available.`}
        </div>
      ) : (
        <div className="overflow-x-auto" style={{ minWidth: 0 }}>
        <div style={{ minWidth: Math.max(720, data.length * 130) }}>
        <ResponsiveContainer width="100%" height={dynamicHeight}>
          <ComposedChart data={data} margin={{ top: 26, right: 18, left: 8, bottom: 60 }}>
              <CartesianGrid stroke={GRID} vertical={false} />
              <XAxis dataKey="short" tick={{ fill: "#475569", fontSize: 10 }}
                     interval={0}
                     tickLine={false} axisLine={{ stroke: "#475569" }} angle={-25} textAnchor="end" height={70} />
              <YAxis tick={{ fill: "#475569", fontSize: 10 }}
                     tickLine={false} axisLine={{ stroke: "#475569" }}
                     tickFormatter={(v) => Intl.NumberFormat("en", { notation: "compact" }).format(v)} />
              <Tooltip
                contentStyle={{ background: "rgba(255,255,255,0.98)", border: "1px solid rgba(13,56,128,0.2)", borderRadius: 2, fontSize: 12, boxShadow: "0 4px 16px rgba(13,56,128,0.10)" }}
                labelFormatter={(_, p) => p?.[0]?.payload?.location_name || ""}
                formatter={(v, name, p) => {
                  const pl = p?.payload || {};
                  if (name === "Sales") return [`${fmtNumber(v)} KWD`, "Sales"];
                  if (name === "Target 1") return [`${fmtNumber(v)} KWD · ${pl.ach_t1_label}`, "Target 1 (floor)"];
                  if (name === "Target 2") return [`${fmtNumber(v)} KWD · ${pl.ach_t2_label}`, "Target 2 (committed)"];
                  if (name === "Target 3") return [`${fmtNumber(v)} KWD · ${pl.ach_t3_label}`, "Target 3 (stretch)"];
                  return [v, name];
                }}
              />
              <Legend wrapperStyle={{ fontSize: 11, color: "#6b7a94", paddingTop: 6 }} />
              <Bar dataKey="sales" name="Sales" fill={NAVY} radius={[3, 3, 0, 0]}>
                {/* Achievement % painted ABOVE each Sales bar in navy so it's
                    visible regardless of bar height. Uses the chosen-tier
                    achievement so it matches the table above. */}
                <LabelList
                  dataKey="pct_label"
                  position="top"
                  fill={NAVY}
                  fontSize={11}
                  fontWeight={700}
                />
              </Bar>
              <Bar dataKey="target_1" name="Target 1" fill={T1} radius={[3, 3, 0, 0]} fillOpacity={0.85} />
              <Bar dataKey="target_2" name="Target 2" fill={T2} radius={[3, 3, 0, 0]} fillOpacity={0.85} />
              <Bar dataKey="target_3" name="Target 3" fill={T3} radius={[3, 3, 0, 0]} fillOpacity={0.85} />
            </ComposedChart>
        </ResponsiveContainer>
        </div>
        </div>
      )}

      <div className="text-[11px] text-muted-foreground mt-3 leading-relaxed">
        <span className="inline-flex items-center gap-1.5"><span className="w-2.5 h-2.5 rounded-sm" style={{ background: NAVY }} /> Sales (period)</span>
        <span className="mx-3">·</span>
        <span className="inline-flex items-center gap-1.5"><span className="w-2.5 h-2.5 rounded-sm" style={{ background: T1 }} /> Target 1 <span className="text-muted-foreground/60">(floor)</span></span>
        <span className="mx-3">·</span>
        <span className="inline-flex items-center gap-1.5"><span className="w-2.5 h-2.5 rounded-sm" style={{ background: T2 }} /> Target 2 <span className="text-muted-foreground/60">(committed)</span></span>
        <span className="mx-3">·</span>
        <span className="inline-flex items-center gap-1.5"><span className="w-2.5 h-2.5 rounded-sm" style={{ background: T3 }} /> Target 3 <span className="text-muted-foreground/60">(stretch)</span></span>
        <span className="mx-3">·</span>
        Number above the navy bar = achievement % vs the active tier above.
      </div>
    </div>
  );
};

export default TargetCompareChart;
